// SPDX-License-Identifier: BUSL-1.1

/**
 * TeamArchivePage — team-safe archive evidence view (CEO-Sprint-70 E3-S1-T1).
 *
 * Path: /teams/:teamSlug/archive
 *
 * Surfaces session context and governance evidence to team members WITHOUT:
 *  - Approval/rollback/governance mutation controls
 *  - Cross-org admin views
 *
 * Consumes the canonical /api/archive/timeline API (ReconstructionQueryService).
 * Does NOT re-derive evidence independently.  Org-level governance controls
 * remain enforced server-side regardless of surface.
 */

import { Link, useParams } from 'react-router';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, ChevronRight, Eye } from 'lucide-react';
import { apiFetch } from '@/lib/api-client';
import { useOrg } from '@/contexts/org-context';
import { relativeTime } from '@/lib/format';
import { cn } from '@/lib/utils';

interface AttributionEntry {
  timelineId: string;
  kind: string;
  occurredAt: string;
  actorId: string;
  actorType: 'human' | 'agent' | 'system';
  summary: string;
  approvalChainId: string | null;
  governanceContext: string | null;
  authority?: 'authoritative' | 'supporting' | 'unknown';
}

interface EvidenceGap {
  type: string;
  entryId?: string;
  reason: string;
}

interface ArchiveResponse {
  entries: AttributionEntry[];
  evidenceGaps?: EvidenceGap[];
}

export function TeamArchivePage(): React.ReactElement {
  const { currentOrg } = useOrg();
  const slug = currentOrg?.slug ?? '';
  const { teamSlug } = useParams<{ teamSlug: string }>();

  const [expanded, setExpanded] = useState<string | null>(null);

  // CEO-S73-E3-S1-T2: Use /api/archive/timeline with teamSlug filter (CEO-S73-E3-S1-T1).
  // Previously used agentProfileId proxy which is incorrect — agent profiles and
  // teams are distinct concepts. teamSlug is the canonical filter for team-scoped views.
  const { data, isLoading, error } = useQuery({
    queryKey: ['team-archive', slug, teamSlug],
    queryFn: () =>
      apiFetch<ArchiveResponse>(
        `/api/archive/timeline?teamSlug=${encodeURIComponent(teamSlug ?? '')}&limit=200`,
      ),
    enabled: !!slug && !!teamSlug,
    staleTime: 10_000,
    retry: 1,
  });

  const entries = data?.entries ?? [];
  const gaps = data?.evidenceGaps ?? [];

  if (!teamSlug) {
    return (
      <div className="p-6">
        <p className="text-sm text-neutral-400">Team not specified.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6 p-6">
      {/* Header — read-only notice */}
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-bold text-neutral-100">
          <Eye className="size-6 text-cyan-400" />
          Team Archive: {teamSlug}
        </h1>
        <p className="mt-1 text-sm text-neutral-400">
          Read-only view of governance evidence scoped to this team.
        </p>
        <p className="mt-1 text-xs text-neutral-500">
          Approval, rollback, and governance mutation controls are restricted to admin surfaces.
        </p>
      </div>

      {/* Read-only boundary */}
      <Card className="border-cyan-700/40 bg-cyan-950/20">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-cyan-300">
            <Eye className="size-4" />
            Read-only evidence surface
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-xs text-neutral-400">
            This view consumes the canonical archive timeline API.{' '}
            Approval, rollback, and governance mutation controls are NOT exposed here.
            Org-level governance controls remain enforced server-side regardless of this surface.
          </p>
        </CardContent>
      </Card>

      {/* Evidence gaps */}
      {gaps.length > 0 && (
        <Card className="border-amber-700/50 bg-amber-950/20">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-amber-300">
              <AlertTriangle className="size-4" />
              Evidence gaps ({gaps.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {gaps.map((gap, i) => (
              <div key={i} className="rounded border border-amber-800/40 bg-amber-900/20 p-3">
                <p className="text-xs font-mono text-amber-400">{gap.type}</p>
                <p className="mt-1 text-sm text-neutral-300">{gap.reason}</p>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Loading / error */}
      {isLoading && (
        <Card>
          <CardContent className="space-y-3 py-6">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-4 w-1/2" />
          </CardContent>
        </Card>
      )}

      {error && (
        <Card className="border-red-700/50 bg-red-950/20">
          <CardContent className="py-6">
            <p className="text-sm text-red-300">Failed to load team archive.</p>
            <p className="mt-1 text-xs text-red-400">{(error as Error).message}</p>
          </CardContent>
        </Card>
      )}

      {!isLoading && !error && entries.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center gap-2 py-12 text-center">
            <Eye className="size-8 text-neutral-700" />
            <p className="text-sm text-neutral-400">No archive entries for this team.</p>
          </CardContent>
        </Card>
      )}

      {/* Governance evidence timeline */}
      {entries.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Governance Evidence ({entries.length})</CardTitle>
            <p className="text-xs text-neutral-500">
              Each entry is from the canonical archive timeline API. Authority labels indicate
              governance-authoritative vs imported supporting narrative.
            </p>
          </CardHeader>
          <CardContent className="p-0">
            <ul className="divide-y divide-neutral-800">
              {entries.map((entry) => (
                <li key={entry.timelineId}>
                  <button
                    className="flex w-full items-start gap-3 px-6 py-4 text-left transition hover:bg-neutral-900/50"
                    onClick={() => setExpanded(expanded === entry.timelineId ? null : entry.timelineId)}
                  >
                    <time className="w-14 shrink-0 pt-0.5 font-mono text-xs text-neutral-500">
                      {relativeTime(entry.occurredAt)}
                    </time>

                    <span className="mt-0.5 shrink-0 rounded bg-neutral-800 px-2 py-0.5 text-xs text-neutral-300">
                      {entry.kind}
                    </span>

                    <span className="mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full bg-neutral-800 text-xs text-neutral-400">
                      {entry.actorType === 'human' ? 'H' : entry.actorType === 'agent' ? 'A' : 'S'}
                    </span>

                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-neutral-300">{entry.summary}</p>
                      <p className="mt-0.5 text-xs text-neutral-600">{entry.actorId}</p>
                    </div>

                    <Badge
                      variant="outline"
                      className={cn(
                        'shrink-0 text-xs',
                        entry.authority === 'authoritative' && 'border-emerald-700/50 text-emerald-400',
                        entry.authority === 'supporting' && 'border-amber-700/50 text-amber-400',
                        entry.authority === 'unknown' && 'border-neutral-700 text-neutral-500',
                      )}
                    >
                      {entry.authority ?? '?'}
                    </Badge>

                    <ChevronRight
                      className={cn(
                        'mt-1 size-4 shrink-0 text-neutral-600 transition-transform',
                        expanded === entry.timelineId && 'rotate-90',
                      )}
                    />
                  </button>

                  {expanded === entry.timelineId && (
                    <div className="mx-6 mb-4 rounded border border-neutral-800 bg-neutral-950 p-3">
                      <p className="mb-2 text-xs font-semibold tracking-wider text-neutral-600 uppercase">
                        Evidence Details (read-only)
                      </p>
                      <pre className="overflow-x-auto text-xs text-neutral-400">
                        {JSON.stringify(entry, null, 2)}
                      </pre>
                      <p className="mt-2 text-xs text-cyan-400">
                        To act on this evidence (approve, rollback, escalate), use the
                        {' '}<Link to="/change-sets" className="underline">change-sets</Link> surface.
                      </p>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Read-only boundary footer */}
      <Card className="border-neutral-800">
        <CardContent className="py-4">
          <p className="text-xs text-neutral-500">
            <strong className="text-neutral-400">Read-only boundary:</strong>{' '}
            approval, rollback, and governance mutation controls are NEVER rendered on this surface.
            Org-level governance controls remain enforced server-side for all requests regardless of
            who consumes this view.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
